import { useState } from "react"

import { Alert, Flex, Form, Segmented, Switch, Typography } from "antd"

import Icon from "../../Icon"
import ButtonComponent from "../Button"
import Button from "../Button"
import InputComponent from "../Input"
import InputNumberComponent from "../Input/InputNumber"

const { Text } = Typography

const defaultValues = {
  count_of_free_alerts: 0,
  type: "Fixed",
  ethoca_price_levels: [{ type: "ethoca", range_to: null, price: null }],
  rdr_1_price_levels: [{ type: "rdr_tier_1", range_to: null, price: null }],
  cdrn_price_levels: [{ type: "cdrn", range_to: null, price: null }]
}

const FormList = ({ id, type, label, onFocusHandler }) => {
  const form = Form.useFormInstance()
  const data = Form.useWatch(id, form)
  const count_of_free_alerts = Form.useWatch("count_of_free_alerts", form)

  return (
    <Form.List name={id}>
      {(fields, { add, remove }) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            rowGap: 8,
            marginBottom: 16
          }}
        >
          {fields.map((field, index) => (
            <Form.Item
              key={field.key}
              label={index === 0 ? label : ""}
              required={true}
              style={{ marginBottom: 0 }}
            >
              <Flex gap={8}>
                <InputNumberComponent
                  placeholder={"Range from"}
                  value={
                    index ? data?.[index - 1]?.["range_to"] + 1 : (count_of_free_alerts || 0) + 1
                  }
                  disabled={true}
                  min={1}
                  step={10}
                  style={{ width: 100 }}
                />

                <Form.Item
                  name={[field.name, "range_to"]}
                  dependencies={["count_of_free_alerts"]}
                  rules={[
                    { required: fields.length !== index + 1 },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          (!index && value > (count_of_free_alerts || 1)) ||
                          (index && value - 2 >= getFieldValue([id, index - 1, "range_to"]))
                        ) {
                          return Promise.resolve()
                        }
                        return Promise.reject(
                          new Error("‘Range To’ should be higher than ‘Range From’ at this level.")
                        )
                      }
                    })
                  ]}
                  noStyle
                >
                  <InputNumberComponent
                    placeholder={"Range to"}
                    //disabled={fields.length > (index + 1)}
                    onFocus={(e) => onFocusHandler(e, [id, field.name, "range_to"])}
                    //onChange={(value) => updateNextLevelFrom(index, value)}
                    min={
                      index ? data?.[index - 1]?.["range_to"] + 2 : (count_of_free_alerts || 0) + 2
                    }
                    step={10}
                    style={{ width: 100 }}
                  />
                </Form.Item>

                <Form.Item name={[field.name, "price"]} rules={[{ required: true }]} noStyle>
                  <InputNumberComponent
                    placeholder={"Price e.g $46.00"}
                    onFocus={(e) => onFocusHandler(e, [id, field.name, "price"])}
                    min={0.01}
                    step={10}
                    precision={2}
                    style={{ flex: "auto" }}
                  />
                </Form.Item>
                {fields.length > 1 ? (
                  <ButtonComponent
                    danger
                    type="link"
                    size="large"
                    icon={<Icon type="delete" />}
                    onClick={() => remove(field.name)}
                    disabled={!index}
                    className="flex center"
                    style={{ width: 40 }}
                  />
                ) : null}
              </Flex>
            </Form.Item>
          ))}
          {fields.length < 6 ? (
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="link"
                size="small"
                disabled={!data?.[fields.length - 1]?.["range_to"]}
                onClick={() => {
                  if (data?.[fields.length - 1]?.["range_to"]) add({ type })
                }}
                style={{ padding: 0 }}
                icon={<Icon type="plus" style={{ fontSize: 20 }} />}
              >
                Add level
              </Button>
            </Form.Item>
          ) : null}
        </div>
      )}
    </Form.List>
  )
}

function PricingForm({ onClose, onSubmit, initialValues, pricingList, optionsType }) {
  const [isAppliedFromScratch, setIsAppliedFromScratch] = useState(
    !!initialValues?.["is_applied_from_scratch"]
  )
  const [form] = Form.useForm()
  const typePricing = Form.useWatch("type", form)

  const onFinish = (values) => {
    let data
    if (typePricing === "Volume based") {
      let { ethoca_price_levels, rdr_1_price_levels, cdrn_price_levels, ...other } = values

      data = {
        ...other,
        is_applied_from_scratch: isAppliedFromScratch,
        pricing_items: [...ethoca_price_levels, ...rdr_1_price_levels, ...cdrn_price_levels]
      }
    } else {
      data = { ...values }
    }
    if (data) onSubmit(data)
  }

  const validateUnique = (_, value) => {
    if (pricingList?.length && pricingList.includes(value?.trim()?.toLowerCase())) {
      if (initialValues) {
        if (initialValues["pricing_schema"].toLowerCase() !== value?.trim()?.toLowerCase()) {
          return Promise.reject(new Error("Pricing scheme name should be unique"))
        }
      } else {
        return Promise.reject(new Error("Pricing scheme name should be unique"))
      }
    }
    return Promise.resolve()
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e, fieldName) {
    const field = fieldName || e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const optionsTypeFinal = optionsType.map((option) => ({ value: option, label: option }))

  const validateMessages = {
    required: addErrorIcon("Required fields were not filled.")
  }

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      form={form}
      {...(initialValues ? { initialValues } : { initialValues: defaultValues })}
    >
      <div style={{ height: "min(85vh, 720px)", overflow: "auto" }}>
        <Form.Item
          name="pricing_schema"
          label="Name"
          rules={[{ required: true }, { validator: validateUnique }]}
        >
          <InputComponent
            placeholder="Enter a new pricing scheme name"
            name="pricing_schema"
            disabled={initialValues?.["pricing_schema"]?.toLowerCase() === "default"}
            onFocus={onFocusHandler}
          />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Segmented
            options={optionsTypeFinal}
            name="type"
            size="large"
            block
            disabled={!!initialValues}
            style={{ fontWeight: 500 }}
          />
        </Form.Item>

        {typePricing === "Volume based" && (
          <Alert
            type="info"
            icon={
              <Switch
                defaultChecked={isAppliedFromScratch}
                checked={isAppliedFromScratch}
                onChange={(checked) => setIsAppliedFromScratch(checked)}
              />
            }
            message={<Text strong>Prices listed below are applied from scratch</Text>}
            description={
              <Text type="secondary">
                The next received alert will be considered as the first alert in the volume
              </Text>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          label="Number of the first alerts to skip"
          name="count_of_free_alerts"
          rules={[{ required: true }]}
        >
          <InputNumberComponent
            onFocus={onFocusHandler}
            min={0}
            step={10}
            style={{ width: "100%" }}
          />
        </Form.Item>

        {typePricing === "Volume based" ? (
          <>
            <FormList
              id="ethoca_price_levels"
              type="ethoca"
              label="Ethoca price levels"
              onFocusHandler={onFocusHandler}
            />

            <FormList
              id="rdr_1_price_levels"
              type="rdr_tier_1"
              label="RDR price"
              onFocusHandler={onFocusHandler}
            />

            <FormList
              id="cdrn_price_levels"
              type="cdrn"
              label="CDRN price"
              onFocusHandler={onFocusHandler}
            />
          </>
        ) : (
          <>
            <Form.Item
              label="Ethoca price ($)"
              name="ethoca_price"
              rules={[
                {
                  required: true,
                  message: addErrorIcon("Required fields were not filled.")
                }
              ]}
            >
              <InputNumberComponent
                onFocus={onFocusHandler}
                min={0.01}
                step={10}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="RDR price ($)" name="rdr_1_price" rules={[{ required: true }]}>
              <InputNumberComponent
                onFocus={onFocusHandler}
                min={0.01}
                step={10}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="CDRN price ($)" name="cdrn_price" rules={[{ required: true }]}>
              <InputNumberComponent
                onFocus={onFocusHandler}
                min={0.01}
                step={10}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        )}
      </div>
      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 3px" }}>
        <ButtonComponent size="large" htmlType="button" onClick={onClose}>
          Cancel
        </ButtonComponent>

        <ButtonComponent size="large" type="primary" htmlType="submit">
          {initialValues ? "Save changes" : "Create pricing scheme"}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default PricingForm
